import React from 'react';
import { Alert, Box } from '@zillow/constellation-10';
import type {
  MetroidAccountIdType,
  MetroidCustomFieldsType,
  MetroidError,
  MetroidPaymentMethodResultType,
  MetroidSessionTokenType,
} from '@zg-rentals/rental-manager-env';
import {
  browserUtils,
  ErrorMessageConstants,
  LoadingConstants,
  logError,
  mapZuoraPaymentMethodError,
  useRentalManagerUser,
  ZUORA_ERROR_CODES,
} from '@zg-rentals/rental-manager-env';
import SpinnerContainer from './SpinnerContainer';
import BillingConstants from '../constants/BillingConstants';
import { usePaymentSessionInfo } from '../hooks/usePaymentSessionInfo';
import { emitAddACHEvent, emitAddCreditCardEvent, EVENT_IDS } from '../model/ClickstreamEvents';
import { AdproductsUserBillingInvoicePaymentApiHandler_logAttemptExceedLimitation } from '@zg-rentals/ts-adproducts-client';

const isLocalhost = typeof window !== 'undefined' ? browserUtils.isLocalhost(window.location.host) : false;

const { PAYMENT_TYPES } = BillingConstants;

const PAYMENT_METHOD_MAPPINGS = {
  [PAYMENT_TYPES.ACH]: 'ACH:Zillow:Zillow Rental Network',
  [PAYMENT_TYPES.CREDIT_CARD]: 'CC:Zillow:Zillow Rental Network',
};

//@ts-ignore
let PaymentMethodIframeProviderWrapper;

export type MetroidIframeWrapperProps = {
  accountNumber: string;
  onCreditCardAdded: (result: MetroidPaymentMethodResultType) => void;
  selectedType: string;
  guestPaySessionId?: string;
};

const localhostPostfix = isLocalhost ? ':localhost' : '';

const MetroidIframeWrapper = ({
  accountNumber,
  onCreditCardAdded,
  selectedType,
  guestPaySessionId,
}: MetroidIframeWrapperProps) => {
  const { user } = useRentalManagerUser();
  const { paymentSessionInfo, paymentSessionInfoLoadingState } = usePaymentSessionInfo(
    accountNumber,
    guestPaySessionId,
  );

  const metroidHPPConfigId = PAYMENT_METHOD_MAPPINGS[selectedType];
  const [isClient, setIsClient] = React.useState(false);
  const [iframeRendered, setIframeRendered] = React.useState(false);
  const iframeRef = React.useRef<HTMLDivElement>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [customFields, setCustomFields] = React.useState<MetroidCustomFieldsType | null>(null);
  const [metroidAccountId, setMetroidAccountId] = React.useState<MetroidAccountIdType | null>(null);
  const [sessionToken, setSessionToken] = React.useState<MetroidSessionTokenType | null>(null);
  const [errorMessage, setErrorMessage] = React.useState('');

  const pollForIframe = () => {
    timer.current = setTimeout(() => {
      if (iframeRef.current && iframeRef.current.clientHeight > 0) {
        setIframeRendered(true);
      }
      pollForIframe();
    }, 200);
  };

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true);
      PaymentMethodIframeProviderWrapper = require('@zillow/metroid-payment-method').default;
    }
    pollForIframe();

    if (selectedType === PAYMENT_TYPES.ACH) {
      emitAddACHEvent({ user });
    } else if (selectedType === PAYMENT_TYPES.CREDIT_CARD) {
      emitAddCreditCardEvent({ user });
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!paymentSessionInfo) return;
    setMetroidAccountId({
      brand: paymentSessionInfo.brand,
      businessUnit: paymentSessionInfo.businessUnit,
      userId: paymentSessionInfo.userId,
    });
    setSessionToken({
      referenceKey: paymentSessionInfo.referenceKey,
      secret: paymentSessionInfo.secret,
    });

    setCustomFields({
      ExternalUserId__c: user.userToken,
    });
  }, [paymentSessionInfo, user.userToken]);

  const handleSubmitFail = (e: MetroidError) => {
    if (e && e.code_details && e.code_details.includes(ZUORA_ERROR_CODES.Attempt_Exceed_Limitation)) {
      logError({
        error: e,
        errorType: `MetroidError_${ZUORA_ERROR_CODES.Attempt_Exceed_Limitation}`,
        context: {
          location: 'MetroidIframeWrapper - handleSubmitFail',
          accountNumber,
          guestPaySessionId,
          selectedType,
        },
      });
      AdproductsUserBillingInvoicePaymentApiHandler_logAttemptExceedLimitation({ accountNumber }).catch(() => {});
    }
    logError({
      error: e,
      errorType: 'MetroidError',
      context: { location: 'MetroidIframeWrapper - handleSubmitFail', accountNumber, guestPaySessionId, selectedType },
    });
    setErrorMessage(mapZuoraPaymentMethodError(e));
  };

  if (isClient) {
    const showIframe =
      paymentSessionInfoLoadingState === LoadingConstants.SUCCESS &&
      metroidAccountId &&
      metroidAccountId &&
      sessionToken;

    const showSpinner =
      paymentSessionInfoLoadingState === LoadingConstants.LOADING ||
      paymentSessionInfoLoadingState === LoadingConstants.INITIAL ||
      (!iframeRendered && !errorMessage);

    return (
      <Box
        css={{
          position: 'relative',
          minHeight: '200px',
          paddingBottom: '400',
        }}
        data-eventid={selectedType === PAYMENT_TYPES.ACH ? EVENT_IDS.ADD_ACH : EVENT_IDS.ADD_CREDIT_CARD}
      >
        {showSpinner && (
          <Box css={{ position: 'absolute', width: '100%' }}>
            <SpinnerContainer />
          </Box>
        )}
        {(errorMessage || paymentSessionInfoLoadingState === LoadingConstants.ERROR) && (
          <Alert tone="critical">{errorMessage || ErrorMessageConstants.DEFAULT_ERROR}</Alert>
        )}
        <div ref={iframeRef} style={{ display: iframeRendered ? 'block' : 'hidden' }}>
          {showIframe && paymentSessionInfo && (
            // @ts-ignore
            <PaymentMethodIframeProviderWrapper
              className="my-credit-card"
              onSubmitSuccess={onCreditCardAdded}
              onSubmitFailure={handleSubmitFail}
              sessionToken={sessionToken}
              metroidAccountId={metroidAccountId}
              metroidConfigUrl={paymentSessionInfo.metroidConfigUrl}
              metroidHPPConfigId={metroidHPPConfigId + localhostPostfix}
              endpointVersion="v1"
              testConfigurations={{}}
              setAsDefault={false}
              customFields={customFields}
              onRenderComplete={() => {}}
            />
          )}
        </div>
      </Box>
    );
  }

  return null;
};

export default MetroidIframeWrapper;
